import type { ReactElement } from "react";

import { useEffect, useState } from "react";

import { DeviationView } from "./views/DeviationView";
import { FollowUpMarksView } from "./views/FollowUpMarksView";
import { RevisionStreamView } from "./views/RevisionStreamView";
import { InventoryView } from "./views/inventory/InventoryView";
import { FindingSitesView, siteKey } from "./views/sites/FindingSitesView";
import { RevisionView } from "./views/revision/RevisionView";
import { STREAM_START, areaPlace, usePlace, type Area, type Place } from "./navigation/place";
import { AREAS, areasFor, railModules } from "./navigation/modules";
import { ScaffoldStatusView } from "./views/ScaffoldStatusView";
import { SignInView } from "./views/SignInView";
import { CredentialsDialog } from "./shell/CredentialsDialog";
import { passkeyOffered, signInWithPasskey } from "./session/passkey";
import { VersionHistoryView } from "./views/VersionHistoryView";
import { runningLabel } from "./views/versions";
import { AdminView } from "./views/admin/AdminView";
import { useSession, type SessionView } from "./session/useSession";
import { httpClient, type ApiClient, type Role, type StatusApi } from "./api/client";
import { NavRail } from "./shell/NavRail";
import { Shell } from "./shell/Shell";
import { TopBar } from "./shell/TopBar";

/**
 * Der Ort wird **hier** gehalten und nicht erst im angemeldeten Teil: Er gehört
 * der Anwendung und nicht ihrer Hülle, und der Einstieg in die Versionshistorie
 * ist ein Ortswechsel wie jeder andere. Ein zweiter Weg in die Chronik daneben
 * wäre eine zweite Wahrheit über den Ort.
 */
type Navigation = ReturnType<typeof usePlace>;

/**
 * Die Anwendung: die Entscheidung angemeldet/nicht angemeldet, die Bereichswahl
 * und die Ansichten darin.
 *
 * **Das Gerüst steht daneben**, in `shell/` — `Shell`, `NavRail`, `TopBar`,
 * `Aura`, `UserFooter`. Es gehört zum Rahmen (ADR-0025) und wird nachgezeichnet;
 * hier wird es nur zusammengesetzt. Bis #40 trug diese Datei es inline, auf
 * zuletzt 580 Zeilen, und die Achsen waren dabei vertauscht.
 *
 * **Vor der Hülle wird entschieden**: Wer nicht angemeldet ist, bekommt das
 * Anmeldebild und nicht die Hülle mit einer Karte darin (ADR-0025, `FRAME.md`
 * unter „Der Zusammenbau": `if (!auth) return <LoginScreen … />` — außerhalb der
 * Shell, kein Rahmen). Der häufigste Strukturfehler an diesem Rahmen ist, das
 * Anmeldebild in die Hülle zu setzen; hier war er gebaut (#39).
 *
 * Ob jemand angemeldet ist, entscheidet die eigene API und nicht diese Datei:
 * Der Zustand kommt aus `useSession`, das dafür alle 60 Sekunden nachsieht,
 * solange die Anwendung im Vordergrund ist. Ein Ausfall der Gegenstelle soll
 * nicht als „nicht angemeldet" erscheinen — die Unterscheidung trägt jetzt das
 * Anmeldebild, denn über ihm steht keine Leiste mehr, die sie nennen könnte.
 *
 * Aus dem Revisionsstrom führt eine Zeile in die Revisionsansicht, aus den
 * Befunden eine Stelle ebenfalls. Wo die Anwendung steht, führt sie nicht selbst:
 * Das tut `usePlace` in der Chronik des Browsers, damit Zurück und Vorwärts
 * dorthin führen, wo man herkam, statt aus der Anwendung heraus.
 *
 * Der **Bestand** steht als eigener Bereich daneben und nicht im Revisionsstrom:
 * Die Befunde eines Bestandslaufs hängen alle an derselben Revision und wären im
 * Strom nicht auffindbar.
 *
 * Die **Versionshistorie** ist ein Ort ohne Eintrag im Menü: Der Einstieg ist die
 * **Fassungszeile** im Benutzermenü am Fuß der Sidebar, und sie ist der einzige
 * (ADR-0029, Spec v1.3, Story 280). Sie hing zwischen #135 und #158 an der
 * Logo-Zeile; der Platz des Unit-Menüs bleibt seither leer, weil es hier keine
 * Mandanten gibt. Auf dem Bildschirm heißt sie „Neuerungen".
 */
export function App({ client = httpClient }: { client?: ApiClient }): ReactElement {
  const session = useSession(client);
  const navigation = usePlace();
  const version = useRunningVersion(client);
  const passkey = usePasskeyOffer();

  // Solange die eigene API noch nicht geantwortet hat, wird weder die Hülle noch
  // das Anmeldebild behauptet. Der Grund ist schon der des Anmeldebilds, damit
  // nichts aufblitzt, was einen Augenblick später ein anderes Bild ist.
  //
  // **Dasselbe gilt für den Biometrieknopf**: Solange nicht feststeht, ob es ihn
  // gibt, wird das Anmeldebild nicht gezeichnet. Ein Knopf, der nachgereicht
  // wird, springt in eine Karte hinein, die schon steht, und verschiebt alles
  // darunter (`FRAME.md`, „Die biometrische Anmeldung").
  if (session.status === "unknown" || (session.status !== "signed_in" && passkey === null)) {
    return <Loading />;
  }

  if (session.status !== "signed_in") {
    return (
      <SignInView
        signIn={(credentials) => client.signIn(credentials)}
        onSignedIn={session.signedIn}
        expired={session.status === "expired"}
        unavailable={session.message}
        passkey={passkey === true ? () => signInWithPasskey(client) : null}
      />
    );
  }

  return (
    <SignedIn
      session={session}
      client={client}
      navigation={navigation}
      version={runningLabel(version)}
    />
  );
}

/**
 * Der Zustand vor der ersten Antwort: der Grund des Anmeldebilds und ein Wort.
 * Keine Leiste — sie stünde einen Augenblick später nicht mehr da.
 */
function Loading(): ReactElement {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-login)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <span style={{ fontSize: "var(--fs-12)", color: "var(--fg-6)" }}>Wird geladen ...</span>
    </div>
  );
}

/**
 * Ob das Anmeldebild den Knopf für die biometrische Anmeldung zeigt — `null`,
 * solange es noch nicht feststeht.
 *
 * Zwei Fragen in einer: Ist auf **diesem Gerät** ein Zugang eingerichtet, und
 * kann dieser Browser die Zeremonie überhaupt anbieten? Die zweite ist asynchron
 * und deshalb der Grund, warum es das `null` gibt.
 *
 * **Ohne Sitzung gefragt und ohne Gegenstelle beantwortet.** Einen Endpunkt, der
 * eingerichtete Zugänge aufzählt, kennt das SilverERP nicht, und vor der
 * Anmeldung gäbe es auch keine Sitzung, unter der man fragen könnte.
 */
function usePasskeyOffer(): boolean | null {
  const [offered, setOffered] = useState<boolean | null>(null);

  useEffect(() => {
    let discarded = false;

    const answer = (value: boolean): void => {
      if (!discarded) {
        setOffered(value);
      }
    };

    // Eine Prüfung, die nicht antwortet, heißt „kein Knopf": Der Weg über das
    // Passwort steht immer offen, und ein Bild, das auf sie wartet, käme nie.
    passkeyOffered().then(answer, () => answer(false));

    return () => {
      discarded = true;
    };
  }, []);

  return offered;
}

/**
 * Die Fassung, die auf dieser Instanz läuft — einmal beim Erscheinen gefragt.
 *
 * Sie kommt aus `GET /api/status` und nicht aus der Historie: Der Stempel ist
 * eine Eigenschaft der laufenden Anwendung, und die Kopfleiste soll ihn nennen
 * können, ohne alle Fassungstexte zu laden. Einmal und nicht im Takt — er ändert
 * sich nur mit einem neuen Abbild, und dann wird die Seite neu geladen.
 *
 * Antwortet die Gegenstelle nicht, bleibt es bei `null`: Dann steht in der
 * Kopfleiste kein Wort über die Fassung. „Entwicklungsstand" wäre an dieser
 * Stelle eine Behauptung — der Verbindungszustand daneben sagt schon, was los ist.
 */
function useRunningVersion(client: StatusApi): string | null {
  const [version, setVersion] = useState<string | null>(null);

  useEffect(() => {
    let discarded = false;

    client.status().then(
      (status) => {
        if (!discarded) {
          setVersion(status.version);
        }
      },
      () => {
        // Kein Wert und keine Meldung: Der Verbindungszustand trägt den Ausfall.
      },
    );

    return () => {
      discarded = true;
    };
  }, [client]);

  return version;
}

/**
 * Der Inhalt für eine angemeldete Person: das Gerüst, die Bereichswahl darin und
 * der gewählte Bereich.
 *
 * **Der Revisionsstrom steht oben und ist der Anfang**: Er ist die erste Ansicht,
 * die ein Entwickler öffnet.
 *
 * Die Verwaltung sieht nur, wer die Rolle `admin` trägt, die Abweichungskennzahl
 * `management` und `admin`. Das ist eine Frage der Anzeige und keine
 * Rechteentscheidung: Die trifft der Server bei jeder Anfrage.
 */
function SignedIn({
  session,
  client,
  navigation,
  version,
}: {
  session: SessionView;
  client: ApiClient;
  navigation: Navigation;
  version: string | null;
}): ReactElement {
  const { place, visit, go, replace } = navigation;
  // Der Schalter sitzt in der Kopfleiste, die Breite an der Spalte. Der eine Ort,
  // der beide sieht, ist der, an dem das Gerüst zusammengesetzt wird.
  const [collapsed, setCollapsed] = useState(false);
  // **Wie die geöffnete Revision heißt** — der zweite Eintrag der Brotkrume
  // (#41). Er steht hier und nicht in der Adresse: Die Adresse trägt die Kennung
  // der Revision (`/revisions/3`), ihre SVN-Nummer steht erst in der geladenen
  // Antwort, und die kennt allein die Ansicht. Ein Bereichswechsel lässt ihn
  // stehen; er wird nur gelesen, solange eine Revision offen ist.
  const [openTitle, setOpenTitle] = useState<string | null>(null);
  // Ob „Anmeldedaten ändern" offen steht. Der Dialog ist kein Ort — er hat keine
  // Adresse und keinen Eintrag in der Modulordnung, und wer ihn schließt, steht
  // wieder da, wo er war.
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  // Solange die eigene Person noch nicht geladen ist, gilt die engste Rolle:
  // Ein Bereich, der kurz aufblitzt und wieder verschwindet, ist schlechter als
  // einer, der einen Augenblick später erscheint.
  const role: Role = session.person?.role ?? "dev";
  // Solange die eigene Person fehlt, gilt „keine SVN-Kennung zugeordnet": Der
  // Filter über den eigenen Commit steht dann abgeschaltet da und verspricht
  // nichts, was er einen Augenblick später nicht halten könnte (ADR-0028).
  const hasSvnIdentity = session.person?.hasSvnIdentity ?? false;
  const areas = areasFor(role);

  // Eine Adresse, die der eigenen Rolle nicht offensteht, zeigt den Strom. Die
  // Adresse bleibt dabei stehen: Die Rolle kommt einen Augenblick später, und
  // dann soll der angeforderte Bereich erscheinen und nicht verloren sein.
  //
  // Die Versionshistorie steht **nicht** in der Modulordnung und ist trotzdem ein
  // Ort: Sie hat keinen Eintrag im Menü, jede Rolle liest sie, und der Einstieg ist
  // die Fassungszeile im Benutzermenü (Spec v1.3, #158).
  const shown =
    place.area === "version-history" || areas.some((one) => one.id === place.area)
      ? place.area
      : "revisions";
  // Die geöffnete Revision gehört zu dem Bereich, aus dem sie geöffnet wurde.
  // Fällt der Bereich mangels Rolle weg, fällt sie mit ihm weg.
  const openRevision = shown === place.area ? place.revision : null;

  // Eine geöffnete Zeile hinterlässt zweierlei: einen Eintrag in der Chronik für
  // den neuen Ort und eine Marke am alten. Ohne die Marke käme man zwar an die
  // Liste zurück, aber nicht an die Zeile — bei fünfundzwanzig fast gleichen
  // Zeilen ist das der halbe Weg.
  const open = (next: Place, row: string): void => {
    replace({ ...place, last: row });
    go({ ...next, last: row });
  };

  return (
    <Shell
      sidebar={
        <NavRail
          modules={railModules(role)}
          active={shown}
          collapsed={collapsed}
          // Die laufende Fassung steht im Benutzermenü, und die Zeile dort ist der
          // Einstieg in die Chronik — ein Ortswechsel wie jeder andere. Die Spalte
          // reicht beides an den Benutzerfuß durch.
          version={version}
          onOpenVersionHistory={() => go(areaPlace("version-history", place))}
          // „Einstellungen ▸ Anmeldedaten ändern" — der biometrische Zugang
          // dieses Geräts (#159).
          onOpenCredentials={() => setCredentialsOpen(true)}
          // Der Benutzerfuß trägt Name, Kennung, Rolle, das Abmelden und seit #158
          // die Fassungszeile. Die Sitzung geht deshalb in die Spalte und nicht
          // mehr in die Kopfleiste.
          session={session}
          // Ein Bereichswechsel schließt die geöffnete Revision, nimmt aber die
          // Filter beider Listen mit: Wer nur einmal hinüberschaut, soll seinen
          // Ausschnitt nicht verlieren. Wer schon dasteht, bekommt keinen zweiten
          // Eintrag in der Chronik — Zurück täte sonst nichts.
          onSelect={(id) =>
            shown === id && openRevision === null ? undefined : go(areaPlace(id, place))
          }
        />
      }
      topBar={
        <TopBar
          trail={trailOf(shown, openRevision !== null, openTitle)}
          // **Der Weg zurück in der Brotkrume** (#41). Er trifft nur das erste
          // Glied, weil das letzte der Ort selbst ist, und er tut dasselbe wie
          // der Rückweg in der Werkzeugleiste der Revision: Er schließt sie und
          // stellt die Liste wieder her, aus der sie geöffnet wurde — mit deren
          // Filtern und mit der Marke an der Zeile.
          onNavigate={() => {
            if (openRevision !== null) {
              go({ ...place, revision: null });
            }
          }}
          onToggleSidebar={() => setCollapsed(!collapsed)}
        />
      }
    >
      {shown === "revisions" && openRevision === null ? (
        // Der Schlüssel ist der Zähler der Ortswechsel: Die Liste übernimmt
        // die Filter beim Aufbauen, also muss sie neu aufgebaut werden, wenn
        // Zurück einen anderen Ausschnitt bringt — und gerade nicht, wenn der
        // Anwender selbst am Filter dreht.
        <RevisionStreamView
          key={visit}
          client={client}
          filter={place.stream}
          onFilter={(stream) => replace({ ...place, stream })}
          marked={place.last}
          hasSvnIdentity={hasSvnIdentity}
          onOpen={(id) =>
            open({ ...place, revision: { id, target: null } }, String(id))
          }
        />
      ) : null}
      {openRevision !== null ? (
        <RevisionView
          key={openRevision.id}
          client={client}
          revisionId={openRevision.id}
          role={role}
          target={openRevision.target}
          // Zurück führt dorthin, wo die Revision geöffnet wurde, und der
          // Knopf sagt das auch: Aus den Befunden heraus wäre „zurück zu den
          // Revisionen" eine Ansage, die nicht stimmt.
          back={backLabel(shown)}
          // Zurück in die Liste, aus der sie geöffnet wurde — mit deren
          // Filtern und mit der Marke an der Zeile.
          onClose={() => go({ ...place, revision: null })}
          // Eine Revision weiter: ein eigener Eintrag in der Chronik, damit
          // Zurück eine Revision zurückführt und nicht in die Liste. Ohne
          // Stelle, denn es ist eine andere Revision — der Anker der alten
          // führte dort ins Leere.
          onOpenRevision={(id) => go({ ...place, revision: { id, target: null } })}
          // Wie sie heißt, meldet sie nach oben: Die Brotkrume trägt damit den
          // Weg Revisionsstrom → Revision (#41).
          onTitle={setOpenTitle}
        />
      ) : null}
      {shown === "sites" && openRevision === null ? (
        <FindingSitesView
          key={visit}
          client={client}
          filter={place.sites}
          onFilter={(sites) => replace({ ...place, sites })}
          marked={place.last}
          hasSvnIdentity={hasSvnIdentity}
          // Der Sprung an die Stelle: Er führt in die Revisionsansicht, und
          // der Anker ist Datei @ Revision samt Zeilenbereich (ADR-0006).
          // Der Bereich bleibt dabei „sites": Wer aus den Befunden springt,
          // steht weiter in den Befunden — und kommt mit „zurück" dorthin.
          onOpenSite={(site) => {
            if (site.revisionId === null) {
              return;
            }

            open(
              {
                ...place,
                revision: {
                  id: site.revisionId,
                  target: {
                    projectId: site.projectId,
                    path: site.path,
                    revision: site.revisionNumber,
                    startLine: site.startLine,
                    endLine: site.endLine,
                  },
                },
              },
              siteKey(site),
            );
          }}
          // Der Sprung in die entsprechend gefilterte Revisionsliste: Nur sie
          // zeigt den Bearbeitungsstand, und eine Revision ohne Befund kommt
          // unter den Befunden gar nicht vor.
          onShowIncomplete={(projectId) => {
            go({
              ...place,
              area: "revisions",
              revision: null,
              stream: { ...STREAM_START, projectId, state: "failed" },
              last: null,
            });
          }}
        />
      ) : null}
      {shown === "inventory" ? <InventoryView client={client} /> : null}
      {shown === "deviation" ? <DeviationView client={client} /> : null}
      {shown === "follow-up-marks" && openRevision === null ? (
        <FollowUpMarksView
          client={client}
          // Der Sprung an den Befund führt in die Revision, in der er liegt,
          // und markiert dort seine Stelle (ADR-0006). Der Bereich bleibt
          // dabei „Nachhalten": Wer von dort springt, kommt mit „zurück"
          // dorthin.
          onOpenMark={(mark) => {
            if (mark.revisionId === null) {
              return;
            }

            open(
              {
                ...place,
                revision: {
                  id: mark.revisionId,
                  target: {
                    projectId: mark.finding.projectId,
                    path: mark.finding.path,
                    revision: mark.finding.revisionNumber,
                    startLine: mark.finding.startLine,
                    endLine: mark.finding.endLine,
                  },
                },
              },
              String(mark.id),
            );
          }}
        />
      ) : null}
      {shown === "version-history" ? <VersionHistoryView client={client} /> : null}
      {shown === "scaffold" ? <ScaffoldStatusView client={client} /> : null}
      {shown === "administration" ? <AdminView client={client} /> : null}
      {credentialsOpen ? (
        <CredentialsDialog client={client} onClose={() => setCredentialsOpen(false)} />
      ) : null}
    </Shell>
  );
}

/**
 * Die Brotkrume eines Ortes: der Bereich, und in einer Detailansicht das, was
 * darin offen ist.
 *
 * **Einstufig auf Übersichten, zweistufig in Detailansichten** (`FRAME.md`,
 * „Kopfleiste"). Zweistufig ist heute genau ein Weg: Revisionsstrom → Revision
 * (#41). Er ist zugleich der Titel des Screens und der Weg zurück — vorher war
 * der einzige Rückweg ein Pfeil im Inhalt, und in der Leiste stand nur der
 * Bereich, aus dem man längst heraus war. Die übrigen Detailansichten zieht #136
 * nach.
 *
 * **Ein Abschnitt ist kein Glied.** Die Verwaltung wählt ihren Abschnitt in der
 * Werkzeugleiste; er hat keine Adresse, und „Verwaltung ▸ Endpunkte" wäre dieselbe
 * Auskunft zweimal.
 *
 * `label` ist `null`, solange die geöffnete Revision noch nicht geladen ist. Dann
 * steht dort „Revision" und keine erfundene Zahl — die Kennung aus der Adresse
 * ist nicht die Nummer, die ein Mensch liest.
 *
 * Das Wort kommt aus der Modulordnung, und wo die Brotkrume ein anderes braucht als
 * das Menü, steht es dort als `crumb`: Der Revisionsstrom heißt im Menü so und als
 * Screen „Revisionen". Die Versionshistorie steht in keinem Modul und trägt ihren
 * Namen deshalb hier — auf dem Bildschirm heißt sie „Neuerungen" (#158).
 */
function trailOf(area: Area, opened: boolean, label: string | null): string[] {
  const found = AREAS.find((one) => one.id === area);
  const trail = [found === undefined ? "Neuerungen" : found.crumb ?? found.label];

  return opened ? [...trail, label ?? "Revision"] : trail;
}

/**
 * Wohin der Rückweg aus einer geöffneten Revision führt — ausgeschrieben.
 *
 * Er nennt den Bereich, aus dem heraus geöffnet wurde, und nicht „die
 * Revisionen": Eine Ansage, die woandershin zeigt als der Knopf führt, ist
 * schlechter als keine.
 */
function backLabel(area: Area): string {
  if (area === "sites") {
    return "Zurück zu den Befunden";
  }

  return area === "follow-up-marks" ? "Zurück zum Nachhalten" : "Zurück zu den Revisionen";
}

